import { axiosInstance } from '../../helpers'
import {
    PayrollWorksheet,
    PayrollWorksheetDay,
    PayrollWorksheetEmployee,
    PayrollWorksheetEmployeeDay,
    PayrollWorksheetEmployeeWrite,
    PayrollWorksheetTotals,
    PayrollWorksheetWeek,
} from './types'

const BASE_PATH = 'workorder/payroll-worksheet'
const WEEK_PATH = 'workorder/payroll-worksheet-week'
const DAY_PATH = 'workorder/payroll-worksheet-day'
const EMPLOYEE_PATH = 'workorder/payroll-worksheet-employee'
const EMPLOYEE_DAY_PATH = 'workorder/payroll-worksheet-employee-day'

export type PayrollRequestOptions = { portfolio?: boolean }

const paramsWithPortfolio = (options?: PayrollRequestOptions) =>
    options?.portfolio ? { params: { portfolio: true } } : {}

const mergeParams = (
    existing: Record<string, unknown>,
    options?: PayrollRequestOptions,
) =>
    options?.portfolio
        ? { params: { ...existing, portfolio: true } }
        : { params: existing }

export const getWorksheetsBySchedule = (
    scheduleId: number,
    options?: PayrollRequestOptions,
) =>
    axiosInstance.get<PayrollWorksheet[]>(`${BASE_PATH}/`, {
        ...mergeParams({ schedule: scheduleId }, options),
    })

export const getWorksheet = (
    worksheetId: number,
    options?: PayrollRequestOptions,
) =>
    axiosInstance.get<PayrollWorksheet>(
        `${BASE_PATH}/${worksheetId}/`,
        paramsWithPortfolio(options),
    )

export const getWorksheetTotals = (
    worksheetId: number,
    options?: PayrollRequestOptions,
) =>
    axiosInstance.get<PayrollWorksheetTotals>(
        `${BASE_PATH}/${worksheetId}/totals/`,
        paramsWithPortfolio(options),
    )

export const createWorksheet = (
    data: {
        schedule: number
        name?: string
        week_start_date?: string
        num_days?: number
    },
    options?: PayrollRequestOptions,
) =>
    axiosInstance.post<PayrollWorksheet>(
        `${BASE_PATH}/`,
        data,
        paramsWithPortfolio(options),
    )

export const generateFromSchedule = (
    data: { schedule: number; days_per_week?: number },
    options?: PayrollRequestOptions,
) =>
    axiosInstance.post<PayrollWorksheet>(
        `${BASE_PATH}/generate_from_schedule/`,
        { days_per_week: 7, ...data },
        paramsWithPortfolio(options),
    )

export const getWeeksByWorksheet = (
    worksheetId: number,
    options?: PayrollRequestOptions,
) =>
    axiosInstance.get<PayrollWorksheetWeek[]>(`${WEEK_PATH}/`, {
        ...mergeParams({ worksheet: worksheetId }, options),
    })

export const getWeek = (weekId: number, options?: PayrollRequestOptions) =>
    axiosInstance.get<PayrollWorksheetWeek>(
        `${WEEK_PATH}/${weekId}/`,
        paramsWithPortfolio(options),
    )

export const createWeek = (
    data: {
        worksheet: number
        week_start_date: string
        name: string
        position: number
    },
    options?: PayrollRequestOptions,
) =>
    axiosInstance.post<PayrollWorksheetWeek>(
        `${WEEK_PATH}/`,
        data,
        paramsWithPortfolio(options),
    )

export const generateWeekDays = (
    weekId: number,
    data: { num_days?: number; start_date?: string },
    options?: PayrollRequestOptions,
) =>
    axiosInstance.post<PayrollWorksheetWeek>(
        `${WEEK_PATH}/${weekId}/generate_days/`,
        data,
        paramsWithPortfolio(options),
    )

export const updateWorksheet = (
    id: number,
    data: Partial<PayrollWorksheet>,
    options?: PayrollRequestOptions,
) =>
    axiosInstance.patch<PayrollWorksheet>(
        `${BASE_PATH}/${id}/`,
        data,
        paramsWithPortfolio(options),
    )

export const deleteWorksheet = (id: number, options?: PayrollRequestOptions) =>
    axiosInstance.delete(`${BASE_PATH}/${id}/`, paramsWithPortfolio(options))

export const createWorksheetDay = (
    data: {
        worksheet_week: number
        date: string
        position: number
    },
    options?: PayrollRequestOptions,
) =>
    axiosInstance.post<PayrollWorksheetDay>(
        `${DAY_PATH}/`,
        data,
        paramsWithPortfolio(options),
    )

export const createWorksheetEmployee = (
    data: {
        worksheet: number
        user?: number
        employee_name?: string
        staff_type?: string
        on_salary: boolean
        regular_rate: string
        overtime_rate: string
        position: number
    },
    options?: PayrollRequestOptions,
) =>
    axiosInstance.post<PayrollWorksheetEmployee>(
        `${EMPLOYEE_PATH}/`,
        data,
        paramsWithPortfolio(options),
    )

/** Update employee. Do not send the 10 server-maintained total fields (projected_* / actual_* totals). */
export const updateWorksheetEmployee = (
    id: number,
    data: Partial<PayrollWorksheetEmployeeWrite>,
    options?: PayrollRequestOptions,
) =>
    axiosInstance.patch<PayrollWorksheetEmployee>(
        `${EMPLOYEE_PATH}/${id}/`,
        data,
        paramsWithPortfolio(options),
    )

export const deleteWorksheetEmployee = (
    id: number,
    options?: PayrollRequestOptions,
) =>
    axiosInstance.delete(
        `${EMPLOYEE_PATH}/${id}/`,
        paramsWithPortfolio(options),
    )

export const createEmployeeDay = (
    data: {
        employee: number
        worksheet_day: number
        projected_hours: string
        actual_hours: string
    },
    options?: PayrollRequestOptions,
) =>
    axiosInstance.post<PayrollWorksheetEmployeeDay>(
        `${EMPLOYEE_DAY_PATH}/`,
        data,
        paramsWithPortfolio(options),
    )

export const updateEmployeeDay = (
    id: number,
    data: Partial<PayrollWorksheetEmployeeDay>,
    options?: PayrollRequestOptions,
) =>
    axiosInstance.patch<PayrollWorksheetEmployeeDay>(
        `${EMPLOYEE_DAY_PATH}/${id}/`,
        data,
        paramsWithPortfolio(options),
    )

const DEFAULT_REG_HOURS_THRESHOLD = 40

/** Total actual pay: uses server-maintained week_totals (or employee totals) when present; else computed from day_entries. */
export function computePayrollTotal(
    worksheet: PayrollWorksheet | null | undefined,
    regHoursThreshold: number = DEFAULT_REG_HOURS_THRESHOLD,
): number {
    if (!worksheet?.weeks) return 0
    let total = 0
    for (const week of worksheet.weeks) {
        for (const emp of week.employees ?? []) {
            const isSalary = emp.on_salary
            const src = emp.week_totals
            const regRate = parseFloat(emp.regular_rate || '0')
            const otRate = isSalary ? 0 : parseFloat(emp.overtime_rate || '0')
            const regPay = parseFloat(src?.actual_reg_pay ?? '')
            const otPay = isSalary ? 0 : parseFloat(src?.actual_ot_pay ?? '')
            if (!Number.isNaN(regPay) && (isSalary || !Number.isNaN(otPay))) {
                total += regPay + otPay
            } else {
                let regHrs = parseFloat(src?.actual_reg_hours ?? '')
                const otHrs = isSalary
                    ? 0
                    : parseFloat(src?.actual_ot_hours ?? '')
                if (isSalary && !Number.isNaN(regHrs)) {
                    regHrs = Math.min(regHrs, regHoursThreshold)
                }
                if (
                    !Number.isNaN(regHrs) &&
                    (isSalary || !Number.isNaN(otHrs))
                ) {
                    total += regHrs * regRate + otHrs * otRate
                } else {
                    const entries = emp.day_entries ?? []
                    const actTotal = entries.reduce(
                        (s, d) => s + parseFloat(d.actual_hours || '0'),
                        0,
                    )
                    const actReg = Math.min(actTotal, regHoursThreshold)
                    const actOt = isSalary
                        ? 0
                        : Math.max(0, actTotal - regHoursThreshold)
                    total += actReg * regRate + actOt * otRate
                }
            }
        }
    }
    return total
}
